import React, { useState } from 'react';
import { Check, X, ChevronDown, ChevronRight, AlertCircle, Clock, Pause, Link2, ListChecks, Plus } from 'lucide-react';
import type { ReconciliationProposal } from '@/services/api/agentApi';
import { Button } from '@/components/ui/button';

function timeAgo(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const ICONS: Record<ReconciliationProposal['proposal_type'], React.ReactNode> = {
  likely_complete: <Check className="h-3 w-3" />,
  overdue: <AlertCircle className="h-3 w-3" />,
  stalled: <Pause className="h-3 w-3" />,
  blocked: <X className="h-3 w-3" />,
  forgotten_commitment: <Clock className="h-3 w-3" />,
  cascade_ready: <Link2 className="h-3 w-3" />,
  proposed_goal_or_task: <Plus className="h-3 w-3" />,
};

const URGENCY_COLOR: Record<ReconciliationProposal['proposal_type'], string> = {
  likely_complete: 'bg-emerald-500/10 text-emerald-600',
  overdue: 'bg-red-500/10 text-red-600',
  stalled: 'bg-amber-500/10 text-amber-600',
  blocked: 'bg-orange-500/10 text-orange-600',
  forgotten_commitment: 'bg-red-500/10 text-red-600',
  cascade_ready: 'bg-blue-500/10 text-blue-600',
  proposed_goal_or_task: 'bg-purple-500/10 text-purple-600',
};

interface Props {
  proposal: ReconciliationProposal;
  onApprove?: (id: string) => void | Promise<void>;
  onReject?: (id: string) => void | Promise<void>;
}

export const ProposalCard: React.FC<Props> = ({ proposal, onApprove, onReject }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const handle = async (action: () => Promise<void> | void) => {
    setBusy(true);
    try { await action(); } finally { setBusy(false); }
  };

  const title = renderTitle(proposal);

  return (
    <div className="border rounded-lg p-4 mb-3 bg-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded font-medium inline-flex items-center gap-1 ${URGENCY_COLOR[proposal.proposal_type]}`}>
              {ICONS[proposal.proposal_type]}
              {proposal.proposal_type.replace(/_/g, ' ')}
            </span>
            <ListChecks className="h-3 w-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{proposal.target_kind}</span>
            <span className="text-xs text-muted-foreground ml-auto">{timeAgo(proposal.decided_at)}</span>
          </div>
          <p className="text-sm font-medium leading-tight mb-2">{title}</p>
          <div className="flex items-center gap-2 text-xs">
            <span>{(proposal.llm_confidence * 100).toFixed(0)}%</span>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              details
            </button>
          </div>
          {expanded && (
            <div className="mt-2 text-xs text-muted-foreground border-l-2 pl-2 space-y-1">
              <div><strong>Reasoning:</strong> {proposal.llm_reasoning}</div>
              {proposal.supporting_memory_ids.length > 0 && (
                <div><strong>Evidence:</strong> {proposal.supporting_memory_ids.length} memory reference(s)</div>
              )}
              {Object.keys(proposal.proposed_change || {}).length > 0 && (
                <pre className="text-xs bg-muted/30 p-2 rounded overflow-x-auto">
                  {JSON.stringify(proposal.proposed_change, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {proposal.status === 'pending_review' && onApprove && (
            <Button size="sm" disabled={busy} onClick={() => handle(() => onApprove(proposal.proposal_id))}>
              <Check className="h-3 w-3 mr-1" /> Approve
            </Button>
          )}
          {proposal.status === 'pending_review' && onReject && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => handle(() => onReject(proposal.proposal_id))}>
              <X className="h-3 w-3 mr-1" /> Dismiss
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

function renderTitle(proposal: ReconciliationProposal): string {
  const change = proposal.proposed_change as Record<string, unknown>;
  switch (proposal.proposal_type) {
    case 'overdue':
      return `Task is ${change?.days_overdue ?? '?'} day(s) overdue`;
    case 'stalled':
      return `Goal stalled — no activity for ${change?.days_since_activity ?? '?'} days`;
    case 'likely_complete':
      return 'Task looks complete — please confirm';
    case 'blocked':
      return `Blocked: ${(change?.blocker as string) || 'see reasoning'}`;
    case 'forgotten_commitment':
      return `Forgotten commitment: ${(change?.proposed_title as string) || proposal.target_id || 'unknown'}`;
    case 'cascade_ready':
      return `Completion unblocks ${(change?.unblocked_task_ids as string[] | undefined)?.length ?? 0} task(s)`;
    case 'proposed_goal_or_task':
      return `New ${(change?.project_name_hint as string) ? `project "${change.project_name_hint}"` : 'goal/task structure'}`;
  }
}
